// /link — sign a headset in from a phone or computer (TV-style).
//   /link?code=K7M42Q   (or /l/K7M42Q, which redirects here) fills the code in.
//
// The headset (Quest app or Quest Browser, components/quest) shows a code and
// polls supabase/functions/device-link. Here the person signs in or creates an
// account any way they like (email, Google — Google comes back to
// /link?code=… through /welcome), then confirms "Link this headset?". The
// function attaches their account; the headset signs itself in within ~2 s.
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { User } from '@supabase/supabase-js';
import { AlertCircle, CheckCircle2, Headset, Loader } from 'lucide-react';
import AuthComponent from '../components/Auth';
import { supabase } from '../config/supabase';
import { approveDeviceLink } from '../services/deviceLink';
import { CODE_LENGTH, cleanCodeInput, formatCode, linkPagePath, parseCode } from '../utils/deviceLinkCode';
import './LoginPage.css';
import './DeviceLink.css';

const DeviceLink: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const [user, setUser] = useState<User | null>(null);
  const [checking, setChecking] = useState(true);
  const [code, setCode] = useState(() => cleanCodeInput(params.get('code') || ''));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linked, setLinked] = useState<{ email: string | null } | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => { setUser(session?.user ?? null); setChecking(false); });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => setUser(session?.user ?? null));
    return () => subscription.unsubscribe();
  }, []);

  // Keep ?code= in the address bar, so a Google / email round trip brings it back.
  const full = parseCode(code);
  useEffect(() => {
    if ((params.get('code') || '') === (full || '')) return;
    setParams(full ? { code: full } : {}, { replace: true });
  }, [full, params, setParams]);
  const next = useMemo(() => linkPagePath(full), [full]);

  const link = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!full || busy) return;
    setBusy(true); setError(null);
    const r = await approveDeviceLink(full);
    setBusy(false);
    if (r.ok) { setLinked({ email: r.email || user?.email || null }); return; }
    if (r.error === 'signin_required') { await supabase.auth.signOut().catch(() => {}); }
    setError(r.message);
  };

  const signOut = async () => { await supabase.auth.signOut().catch(() => {}); setError(null); };

  const codeField = (
    <label className="gxl-code">
      <span className="gxa-label">Code from your headset</span>
      <input
        value={formatCode(code)} placeholder="K7M-42Q" inputMode="text" autoCapitalize="characters" autoComplete="one-time-code"
        autoCorrect="off" spellCheck={false} maxLength={CODE_LENGTH + 1} aria-invalid={!!error}
        autoFocus={!code}
        onChange={(e) => { setCode(cleanCodeInput(e.target.value)); setError(null); }}
      />
    </label>
  );

  let body: React.ReactNode;
  if (checking) {
    body = <div className="gx-login-wait"><Loader size={22} className="spin" /></div>;
  } else if (linked) {
    body = <div className="gxa gxl">
      <div className="gxl-done" role="status">
        <CheckCircle2 size={56} aria-hidden />
        <h2>Headset linked</h2>
        <p>Your headset is signing in{linked.email ? <> as <strong>{linked.email}</strong></> : null}. Put it back on: it takes a couple of seconds.</p>
        <p className="gxl-fine">You can close this page.</p>
      </div>
    </div>;
  } else if (!user) {
    body = <div className="gxl-stack">
      <div className="gxa gxl">
        <div className="gxa-head">
          <h2><Headset size={24} aria-hidden /> Sign in your headset</h2>
          <p>Enter the code showing in your headset, then sign in or create an account below. Nothing to type in VR.</p>
        </div>
        {codeField}
      </div>
      <AuthComponent next={next} showHeading={false} />
    </div>;
  } else {
    body = <div className="gxa gxl">
      <div className="gxa-head">
        <h2><Headset size={24} aria-hidden /> Link this headset?</h2>
        <p>It will sign in as <strong>{user.email}</strong>. Only link a headset you're using right now.</p>
      </div>
      <form className="gxa-form" onSubmit={link}>
        {codeField}
        {error && <div className="gxa-alert" role="alert"><AlertCircle size={16} /> {error}</div>}
        <button className="gxa-submit" disabled={!full || busy}>
          {busy && <Loader size={17} className="spin" />} {busy ? 'Linking…' : 'Link'}
        </button>
      </form>
      <button type="button" className="gxl-switch" onClick={() => void signOut()}>Not you? Use a different account</button>
    </div>;
  }

  return (
    <div className="gx-login">
      <video className="gx-login-bg" autoPlay muted loop playsInline poster="/auth/auth-poster.webp" aria-hidden>
        <source src="/auth/auth-loop.webm" type="video/webm" />
        <source src="/auth/auth-loop.mp4" type="video/mp4" />
      </video>
      <div className="gx-login-shade" aria-hidden />
      <header className="gx-login-top">
        <a href="/" className="gx-lockup gx-login-lockup" aria-label="GathrXR home">
          <img src="/brand/gathrxr-mark.png" alt="" />
          <span className="gx-word">Gathr<span>XR</span></span>
        </a>
      </header>
      <main className="gxl-main gx-login-card">{body}</main>
    </div>
  );
};

export default DeviceLink;
